/**
 * QAPipelineDashboard — Enterprise QA Pipeline for the AI Knowledge Base.
 *
 * Rolls up every `/api/ai/ask` call plus the KB coverage report into a
 * single admin dashboard so leadership can answer four questions at a
 * glance:
 *
 *   1. Are we getting BETTER or WORSE at grounding AI answers over time?
 *   2. Which subscription tier has the biggest coverage gap right now?
 *   3. Which specific unanswered questions come up over and over?
 *   4. Which uploaded documents are actually earning their keep — and
 *      which are dead weight nobody has cited?
 *
 * The CSV export downstream powers the compliance / SOC 2 audit trail.
 */
import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  Activity, AlertTriangle, Bell, CheckCircle2, Download, RefreshCw,
  TrendingUp, FileWarning, Users, Sparkles, ArrowUp, ArrowDown, Minus,
} from 'lucide-react';

// ---- Utility helpers --------------------------------------------------

const fmtPct = (n) => `${(n ?? 0).toFixed(1)}%`;
const fmtScore = (n) => (n ?? 0).toFixed(3);
const humanDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};
const shortDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); } catch { return iso; }
};

// Tiny inline SVG sparkline — no chart-lib dependency.
function Sparkline({ points, height = 44, colour = '#10b981' }) {
  if (!points?.length) return null;
  const w = Math.max(160, points.length * 8);
  const max = Math.max(...points, 1);
  const min = Math.min(...points, 0);
  const range = Math.max(max - min, 1);
  const stepX = w / Math.max(points.length - 1, 1);
  const path = points
    .map((v, i) => `${i === 0 ? 'M' : 'L'} ${i * stepX} ${height - ((v - min) / range) * (height - 4) - 2}`)
    .join(' ');
  return (
    <svg width={w} height={height} className="overflow-visible">
      <path d={path} fill="none" stroke={colour} strokeWidth="2" />
      {points.map((v, i) => (
        <circle key={i} cx={i * stepX} cy={height - ((v - min) / range) * (height - 4) - 2} r="1.5" fill={colour} />
      ))}
    </svg>
  );
}

function StatTile({ label, value, sub, tone = 'slate', icon: Icon, testId, delta, deltaUnit = '', deltaGoodDirection = 'up' }) {
  const toneMap = {
    slate:   'border-slate-200 dark:border-slate-700',
    emerald: 'border-emerald-300 bg-emerald-50/60 dark:bg-emerald-900/10',
    rose:    'border-rose-300 bg-rose-50/60 dark:bg-rose-900/10',
    amber:   'border-amber-300 bg-amber-50/60 dark:bg-amber-900/10',
    indigo:  'border-indigo-300 bg-indigo-50/60 dark:bg-indigo-900/10',
  };
  return (
    <div className={`rounded-lg border p-3 ${toneMap[tone] || toneMap.slate}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide font-semibold text-slate-600 dark:text-slate-300">
        {Icon ? <Icon className="w-3.5 h-3.5" /> : null}{label}
      </div>
      <div className="flex items-baseline gap-2 mt-1">
        <div className="text-2xl font-bold text-slate-900 dark:text-white">{value}</div>
        {delta !== undefined && delta !== null ? (
          <DeltaChip value={delta} unit={deltaUnit} goodDirection={deltaGoodDirection} />
        ) : null}
      </div>
      {sub ? <div className="text-[11px] text-slate-500 mt-1">{sub}</div> : null}
    </div>
  );
}

function DeltaChip({ value, unit = '', goodDirection = 'up' }) {
  // `value` is the delta (current − previous). We interpret whether that
  // delta is "good" (green) or "bad" (red) based on the metric semantics
  // supplied by the caller.
  const zero = Math.abs(value) < 0.01;
  const positive = value > 0;
  const isGood = zero
    ? null
    : (goodDirection === 'up' ? positive : !positive);
  const Icon = zero ? Minus : (positive ? ArrowUp : ArrowDown);
  const cls = zero
    ? 'text-slate-400 bg-slate-100 dark:bg-slate-800'
    : isGood
      ? 'text-emerald-700 bg-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-200'
      : 'text-rose-700 bg-rose-100 dark:bg-rose-900/40 dark:text-rose-200';
  const sign = zero ? '' : (positive ? '+' : '');
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded ${cls}`}
      title={`vs previous window`}
    >
      <Icon className="w-3 h-3" />
      {sign}{Number(value).toFixed(unit === 'pt' ? 1 : (unit === '' ? 0 : 2))}{unit}
    </span>
  );
}

const TIER_COLOURS = {
  free:       'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  pro:        'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200',
  business:   'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  enterprise: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  admin:      'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200',
  moderator:  'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-200',
};
const TierPill = ({ tier }) => (
  <span className={`inline-block text-[10px] uppercase tracking-wide px-2 py-0.5 rounded ${TIER_COLOURS[tier] || TIER_COLOURS.free}`}>
    {tier}
  </span>
);

// ---- Main component ---------------------------------------------------

export default function QAPipelineDashboard() {
  const { API } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);

  const load = async (windowDays) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/ai-knowledge/qa-dashboard`, {
        params: { days: windowDays ?? days },
      });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load QA dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); }, []); // eslint-disable-line

  const changeWindow = (n) => {
    setDays(n);
    load(n);
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/ai-knowledge/qa-export.csv`, {
        params: { days },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `kb-qa-export-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('QA export downloaded');
    } catch {
      toast.error('CSV export failed');
    }
  };

  const notifyAdmins = async () => {
    try {
      const res = await axios.post(`${API}/admin/ai-knowledge/qa-notify-now`, null, {
        params: { days: 7 },
      });
      const { alerts, notified, skipped } = res.data || {};
      if (!alerts) {
        toast.success('All clear — nothing to notify.');
      } else if (notified > 0) {
        toast.success(`Notified admins about ${notified} alert${notified === 1 ? '' : 's'}.`);
      } else {
        toast.info(`No new pings sent — ${skipped} alert${skipped === 1 ? ' was' : 's were'} already delivered in the last 24h.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Notify failed');
    }
  };

  const s = data?.summary || {};
  const d = data?.deltas || {};
  const alerts = data?.alerts || [];
  const trend = data?.trend || [];
  const groundedTrend = trend.map((t) => t.grounded_pct);

  return (
    <Card data-testid="qa-pipeline-dashboard">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-indigo-500" />
              Enterprise QA Pipeline
            </CardTitle>
            <CardDescription>
              End-to-end observability for the AI Knowledge Base — track grounding rate over time,
              per-tier coverage gaps, recurring unanswered questions, and document contribution.
              Export the raw ask log for compliance evidence.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 text-xs" data-testid="qa-window-picker">
              {[7, 30, 90].map((n) => (
                <button
                  key={n}
                  onClick={() => changeWindow(n)}
                  disabled={loading}
                  className={`px-3 py-1.5 ${days === n ? 'bg-indigo-500 text-white' : 'bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                  data-testid={`qa-window-${n}`}
                >
                  {n}d
                </button>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={() => load(days)} disabled={loading} data-testid="qa-refresh">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={notifyAdmins}
              disabled={loading || !data}
              data-testid="qa-notify-admins"
              title="Ping every admin & moderator's notification bell with the current active alerts. Same-kind pings dedupe for 24h."
            >
              <Bell className="w-4 h-4 mr-2" />
              Notify admins
            </Button>
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={loading || !data} data-testid="qa-export-csv">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {!data ? (
          <p className="text-sm text-slate-500 py-6 text-center">Loading QA metrics…</p>
        ) : (
          <>
            {/* Regression alerts banner — shown only when the backend
                detected a week-over-week regression. High severity gets
                the red treatment; medium/low use amber. */}
            {alerts.length > 0 ? (
              <div className="space-y-2" data-testid="qa-alerts">
                {alerts.map((a, i) => {
                  const isHigh = a.severity === 'high';
                  return (
                    <div
                      key={i}
                      className={`rounded-lg border p-3 flex items-start gap-3 ${
                        isHigh
                          ? 'border-rose-400 bg-rose-50 dark:bg-rose-900/20'
                          : 'border-amber-300 bg-amber-50 dark:bg-amber-900/20'
                      }`}
                      data-testid={`qa-alert-${a.kind}`}
                    >
                      <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 ${isHigh ? 'text-rose-600' : 'text-amber-600'}`} />
                      <div className="flex-1">
                        <div className={`text-xs uppercase tracking-wide font-semibold ${isHigh ? 'text-rose-800 dark:text-rose-200' : 'text-amber-800 dark:text-amber-200'}`}>
                          {a.severity} · {a.kind.replaceAll('_', ' ')}
                        </div>
                        <div className="text-sm text-slate-800 dark:text-slate-200 mt-0.5">{a.message}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}

            {/* Summary tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="qa-summary-tiles">
              <StatTile
                label="Total asks"
                value={s.total_asks}
                sub={`${s.distinct_askers} distinct askers`}
                icon={Activity}
                tone="indigo"
                testId="qa-tile-total"
                delta={d.total_asks}
                deltaUnit=""
                deltaGoodDirection="up"
              />
              <StatTile
                label="Grounded"
                value={fmtPct(s.grounded_pct)}
                sub={`${s.grounded} of ${s.total_asks} answered from KB`}
                icon={CheckCircle2}
                tone={s.grounded_pct >= 80 ? 'emerald' : s.grounded_pct >= 50 ? 'amber' : 'rose'}
                testId="qa-tile-grounded"
                delta={d.grounded_pct}
                deltaUnit="pt"
                deltaGoodDirection="up"
              />
              <StatTile
                label="Open flags"
                value={s.open_flags}
                sub={`${s.resolved_flags} resolved to date`}
                icon={AlertTriangle}
                tone={s.open_flags === 0 ? 'emerald' : s.open_flags > 10 ? 'rose' : 'amber'}
                testId="qa-tile-open-flags"
              />
              <StatTile
                label="Dead-weight docs"
                value={s.dead_weight_docs}
                sub={`${s.documents} total documents ingested`}
                icon={FileWarning}
                tone={s.dead_weight_docs === 0 ? 'emerald' : 'amber'}
                testId="qa-tile-dead-weight"
                delta={d.dead_weight_docs}
                deltaUnit=""
                deltaGoodDirection="down"
              />
            </div>

            {/* Trend sparkline */}
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-sm font-semibold flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" /> Grounded rate over the last {data.days} days
                  </div>
                  <div className="text-[11px] text-slate-500">
                    Higher is better. A downward trend usually means the community is asking about
                    topics you haven&apos;t documented yet — check the ungrounded clusters below.
                  </div>
                </div>
                <div className="text-2xl font-bold text-emerald-500">{fmtPct(s.grounded_pct)}</div>
              </div>
              <div data-testid="qa-trend-sparkline" className="overflow-x-auto">
                <Sparkline points={groundedTrend} height={48} colour="#10b981" />
              </div>
              <div className="flex justify-between text-[10px] text-slate-500 mt-1">
                <span>{shortDate(trend[0]?.date)}</span>
                <span>Avg score across window: {fmtScore(s.avg_best_score)}</span>
                <span>{shortDate(trend[trend.length - 1]?.date)}</span>
              </div>
            </div>

            {/* By tier */}
            <div>
              <div className="text-sm font-semibold flex items-center gap-2 mb-2">
                <Users className="w-4 h-4" /> Coverage gap by subscription tier
              </div>
              {data.by_tier.length === 0 ? (
                <p className="text-xs text-slate-500 italic py-3">No asks recorded yet in the window.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="qa-by-tier-table">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200 dark:border-slate-700">
                        <th className="pb-2">Tier</th>
                        <th className="pb-2 text-right">Asks</th>
                        <th className="pb-2 text-right">Grounded</th>
                        <th className="pb-2 text-right">Grounded %</th>
                        <th className="pb-2">Coverage bar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.by_tier.map((r) => (
                        <tr key={r.tier} className="border-b border-slate-100 dark:border-slate-800 last:border-0">
                          <td className="py-2"><TierPill tier={r.tier} /></td>
                          <td className="py-2 text-right tabular-nums">{r.asks}</td>
                          <td className="py-2 text-right tabular-nums text-emerald-600 dark:text-emerald-400">{r.grounded}</td>
                          <td className="py-2 text-right">
                            <div className="inline-flex items-center gap-2 justify-end">
                              <span className="tabular-nums font-semibold">{fmtPct(r.grounded_pct)}</span>
                              {r.grounded_pct_delta !== null && r.grounded_pct_delta !== undefined ? (
                                <DeltaChip value={r.grounded_pct_delta} unit="pt" goodDirection="up" />
                              ) : null}
                            </div>
                          </td>
                          <td className="py-2 min-w-[120px]">
                            <div className="h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                              <div
                                className={`h-full ${r.grounded_pct >= 80 ? 'bg-emerald-500' : r.grounded_pct >= 50 ? 'bg-amber-500' : 'bg-rose-500'}`}
                                style={{ width: `${Math.min(100, r.grounded_pct)}%` }}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Top ungrounded clusters */}
            <div>
              <div className="text-sm font-semibold flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-rose-500" /> Recurring unanswered questions
              </div>
              <p className="text-[11px] text-slate-500 mb-2">
                Grouped by semantic key — one row here often means multiple members asking the same
                thing. Write content for the top of this list first.
              </p>
              {data.top_ungrounded.length === 0 ? (
                <p className="text-xs text-emerald-600 dark:text-emerald-400 italic py-3">
                  🎉 No ungrounded questions in the window — every ask was answered from your KB.
                </p>
              ) : (
                <div className="space-y-2" data-testid="qa-top-ungrounded">
                  {data.top_ungrounded.map((r, i) => (
                    <div key={i} className="rounded-lg border border-rose-200 dark:border-rose-900/50 p-3 bg-rose-50/40 dark:bg-rose-900/5">
                      <div className="flex justify-between items-start gap-3">
                        <p className="text-sm font-medium">&ldquo;{r.representative_question}&rdquo;</p>
                        <span className="text-[10px] uppercase tracking-wide bg-rose-200 dark:bg-rose-900/60 text-rose-800 dark:text-rose-200 px-2 py-0.5 rounded whitespace-nowrap">
                          asked {r.count}×
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-1 text-[11px] text-slate-500">
                        <span>Tiers:</span>
                        {r.tiers.map((t) => <TierPill key={t} tier={t} />)}
                        <span>· avg score {fmtScore(r.avg_best_score)}</span>
                        <span>· last asked {shortDate(r.last_asked)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Document contribution */}
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <div className="text-sm font-semibold flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Top-cited documents
                </div>
                {data.docs_leaderboard.length === 0 ? (
                  <p className="text-xs text-slate-500 italic py-3">No grounded answers in window.</p>
                ) : (
                  <ul className="space-y-1 text-sm" data-testid="qa-docs-leaderboard">
                    {data.docs_leaderboard.map((d) => (
                      <li key={d.source_id} className="flex justify-between items-center gap-2 rounded border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50/40 dark:bg-emerald-900/5 px-2 py-1.5">
                        <span className="truncate">{d.source_title}</span>
                        <span className="text-xs text-emerald-700 dark:text-emerald-300 whitespace-nowrap">
                          {d.citation_count} cite{d.citation_count === 1 ? '' : 's'} · {fmtScore(d.avg_score)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <div className="text-sm font-semibold flex items-center gap-2 mb-2">
                  <FileWarning className="w-4 h-4 text-amber-500" /> Dead-weight documents
                </div>
                <p className="text-[11px] text-slate-500 mb-2">
                  Uploaded &gt; 7 days ago but never cited in the window — candidates to prune,
                  retitle, or split into more searchable chunks.
                </p>
                {data.dead_weight.length === 0 ? (
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 italic py-3">
                    ✅ Every document has been earning its keep in this window.
                  </p>
                ) : (
                  <ul className="space-y-1 text-sm" data-testid="qa-dead-weight">
                    {data.dead_weight.map((d) => (
                      <li key={d.source_id} className="flex justify-between items-center gap-2 rounded border border-amber-200 dark:border-amber-900/50 bg-amber-50/40 dark:bg-amber-900/5 px-2 py-1.5">
                        <span className="truncate">{d.title}</span>
                        <span className="text-xs text-amber-700 dark:text-amber-300 whitespace-nowrap">
                          {d.chunk_count} chunks · uploaded {shortDate(d.created_at)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <p className="text-[11px] text-slate-400 text-right">
              Generated {humanDate(data.generated_at)} · window {data.days} days.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
